import React, { useEffect, useState } from 'react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Badge } from '@/components/ui/badge';
import { Trash2, Plus, Save, Clock, Globe } from 'lucide-react';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';

interface MonitoringConfig {
  id?: string;
  url_pattern: string;
  check_frequency_hours: number;
  monitoring_enabled: boolean;
  created_at?: string;
  updated_at?: string;
}

const SEOMonitoringConfig: React.FC = () => {
  const [configs, setConfigs] = useState<MonitoringConfig[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState<string | null>(null);

  useEffect(() => {
    fetchConfigs();
  }, []);

  const fetchConfigs = async () => {
    try {
      const { data, error } = await supabase
        .from('seo_monitoring_config')
        .select('*')
        .order('created_at', { ascending: false });

      if (error) throw error;
      setConfigs(data || []);
    } catch (error: any) {
      console.error('Error fetching configs:', error);
      toast.error('Failed to load monitoring configs');
    } finally {
      setLoading(false);
    }
  };

  const saveConfig = async (config: MonitoringConfig) => {
    try {
      setSaving(config.id || 'new');
      
      if (config.id) {
        // Update existing
        const { error } = await supabase
          .from('seo_monitoring_config')
          .update({
            url_pattern: config.url_pattern,
            check_frequency_hours: config.check_frequency_hours,
            monitoring_enabled: config.monitoring_enabled
          })
          .eq('id', config.id);

        if (error) throw error;
        toast.success('Monitoring config updated');
      } else {
        // Create new
        const { error } = await supabase
          .from('seo_monitoring_config')
          .insert([{
            url_pattern: config.url_pattern,
            check_frequency_hours: config.check_frequency_hours,
            monitoring_enabled: config.monitoring_enabled
          }]);

        if (error) throw error;
        toast.success('Monitoring config created');
      }

      fetchConfigs();
    } catch (error: any) {
      console.error('Error saving config:', error);
      toast.error('Failed to save config');
    } finally {
      setSaving(null);
    }
  };

  const deleteConfig = async (id: string) => {
    try {
      const { error } = await supabase
        .from('seo_monitoring_config')
        .delete()
        .eq('id', id);

      if (error) throw error;
      toast.success('Monitoring config deleted');
      fetchConfigs();
    } catch (error: any) {
      console.error('Error deleting config:', error);
      toast.error('Failed to delete config');
    }
  };

  const addNewConfig = () => {
    const newConfig: MonitoringConfig = {
      url_pattern: 'https://seniorthrive.com/*',
      check_frequency_hours: 24,
      monitoring_enabled: true
    };
    setConfigs([newConfig, ...configs]);
  };

  const updateConfig = (index: number, updates: Partial<MonitoringConfig>) => {
    const updated = [...configs];
    updated[index] = { ...updated[index], ...updates };
    setConfigs(updated);
  };

  const triggerMonitoring = async () => {
    try {
      const { error } = await supabase.functions.invoke('seo-monitoring', {
        body: { action: 'monitor_site' }
      });

      if (error) throw error;
      toast.success('Monitoring task started');
    } catch (error: any) {
      console.error('Error triggering monitoring:', error);
      toast.error('Failed to start monitoring');
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="p-6">
          <div className="animate-pulse space-y-4">
            <div className="h-4 bg-muted rounded w-1/4"></div>
            <div className="h-10 bg-muted rounded"></div>
          </div>
        </CardContent>
      </Card>
    );
  }

  return (
    <div className="space-y-6">
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <Clock className="h-5 w-5" />
            SEO Monitoring Configuration
          </CardTitle>
          <CardDescription>
            Configure automated SEO monitoring patterns and frequencies
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex gap-2">
            <Button onClick={addNewConfig} className="gap-2">
              <Plus className="h-4 w-4" />
              Add Pattern
            </Button>
            <Button onClick={triggerMonitoring} variant="outline" className="gap-2">
              <Globe className="h-4 w-4" />
              Run Now
            </Button>
          </div>

          <div className="space-y-4">
            {configs.map((config, index) => (
              <div key={config.id || `new-${index}`} className="border rounded-lg p-4 space-y-4">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div className="space-y-2">
                    <Label htmlFor={`pattern-${index}`}>URL Pattern</Label>
                    <Input
                      id={`pattern-${index}`}
                      value={config.url_pattern}
                      onChange={(e) => updateConfig(index, { url_pattern: e.target.value })}
                      placeholder="https://example.com/*"
                    />
                  </div>
                  
                  <div className="space-y-2">
                    <Label htmlFor={`frequency-${index}`}>Check Frequency (hours)</Label>
                    <Input
                      id={`frequency-${index}`}
                      type="number"
                      min="1"
                      max="168"
                      value={config.check_frequency_hours}
                      onChange={(e) => updateConfig(index, { check_frequency_hours: Number(e.target.value) })}
                    />
                  </div>

                  <div className="space-y-2">
                    <Label>Status</Label>
                    <div className="flex items-center space-x-4">
                      <Switch
                        checked={config.monitoring_enabled}
                        onCheckedChange={(checked) => updateConfig(index, { monitoring_enabled: checked })}
                      />
                      <Badge variant={config.monitoring_enabled ? "default" : "secondary"}>
                        {config.monitoring_enabled ? 'Enabled' : 'Disabled'}
                      </Badge>
                    </div>
                  </div>
                </div>

                <div className="flex justify-between items-center">
                  <div className="text-sm text-muted-foreground">
                    {config.created_at && (
                      <>Created: {new Date(config.created_at).toLocaleDateString()}</>
                    )}
                  </div>
                  <div className="flex gap-2">
                    <Button
                      onClick={() => saveConfig(config)}
                      disabled={saving === config.id || saving === 'new'}
                      size="sm"
                      className="gap-2"
                    >
                      <Save className="h-4 w-4" />
                      {saving === (config.id || 'new') ? 'Saving...' : 'Save'}
                    </Button>
                    {config.id && (
                      <Button
                        onClick={() => deleteConfig(config.id!)}
                        variant="outline"
                        size="sm"
                        className="gap-2"
                      >
                        <Trash2 className="h-4 w-4" />
                        Delete
                      </Button>
                    )}
                  </div>
                </div>
              </div>
            ))}

            {configs.length === 0 && (
              <div className="text-center py-8 text-muted-foreground">
                No monitoring configurations found. Click "Add Pattern" to create one.
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default SEOMonitoringConfig;